import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { GradientDots } from '@/components/ui/GradientDots';
import { OperatorWordmark } from '@/components/auth/OperatorWordmark';
import { spaceApi } from '@/services/api';

const PENDING_KEY = 'optr.oauth.pending';
// Generous TTL — covers a slow OAuth consent dance (provider sign-in,
// 2FA, multi-screen permission grants). Anything older is treated as
// stale and ignored.
const PENDING_TTL_MS = 30 * 60 * 1000;

/**
 * Public landing for Bedrock AgentCore's post-consent redirect (MCP server
 * OAuth). The popup tab opened by `openAuthorizationUrl` in McpServerDetail
 * lands here after the user completes (or cancels) consent. The original tab
 * listens for the auth-status WS event to refresh itself, so this page's
 * only job is to render a friendly confirmation state.
 *
 * URL is whitelisted on the AgentCore workload identity (stg + dev). To add
 * a new origin you'd redeploy the workload identity, not the frontend.
 *
 * `window.location.search` is logged on mount so we can refine the
 * server-id extraction once we see what AgentCore actually sends.
 */
export default function OAuthConnected() {
  const params = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );

  useEffect(() => {
    // First-version diagnostic — keep until we've nailed the param names.
     
    console.debug(
      '[oauth/connected] params:',
      Object.fromEntries(params.entries()),
    );
  }, [params]);

  // OAuth-spec error params win over everything else. Some upstreams use
  // ?status=error instead of ?error=... — accept both.
  const errorCode =
    params.get('error') ??
    (params.get('status') === 'error' ? 'error' : null);
  const errorDescription = params.get('error_description') ?? undefined;
  const variant: 'success' | 'error' = errorCode ? 'error' : 'success';

  // AgentCore's actual return URL only carries an opaque PAR `session_id` —
  // nothing identifies the MCP server. The opening tab stashes context in
  // localStorage right before opening the popup; we consume it here.
  const pending = useMemo(
    () => (variant === 'success' ? consumePending() : null),
    [variant],
  );
  const serverName = useServerName(pending);

  const successTitle = serverName
    ? `Connected to ${serverName}`
    : 'Connected';
  const successBody = 'You may now close this tab and return to the app.';

  const errorTitle = 'Connection cancelled';
  const errorBody =
    errorDescription ?? 'You can retry from the MCP server settings.';

  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12"
      style={{ backgroundColor: 'hsl(var(--auth-bg))' }}
    >
      <GradientDots />

      <div className="relative z-10 w-full max-w-3xl rounded-[32px] border border-foreground/15 bg-card/95 dark:bg-card/15 p-6 shadow-[0_80px_160px_-30px_rgba(0,0,0,0.8),0_40px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-3xl sm:p-12 md:p-16 lg:p-20 2xl:p-28 2xl:max-w-4xl">
        <div className="flex flex-col items-center text-center">
          <OperatorWordmark size="xl" />
          <p className="mt-6 text-[10px] tracking-[0.15em] sm:text-xs sm:tracking-[0.25em] uppercase text-muted-foreground">
            Make AI Actually Do Things
          </p>
        </div>

        <div className="mt-10 flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-foreground/15 bg-card/60 shadow-sm">
            {variant === 'success' ? (
              <CheckCircle2
                className="h-8 w-8 text-[hsl(150_60%_55%)]"
                aria-hidden
              />
            ) : (
              <XCircle
                className="h-8 w-8 text-[hsl(335_85%_70%)]"
                aria-hidden
              />
            )}
          </div>

          <h1 className="mt-6 text-2xl font-semibold text-foreground">
            {variant === 'success' ? successTitle : errorTitle}
          </h1>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            {variant === 'success' ? successBody : errorBody}
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface PendingOAuth {
  spaceId: string;
  serverId: string;
  ts: number;
}

/**
 * Read and clear the pending-OAuth stash that McpServerDetail wrote before
 * opening the popup. Returns undefined if the stash is missing, malformed,
 * or stale (TTL exceeded) — in any of those cases the landing page falls
 * back to generic copy. Always clears the slot so the next OAuth flow
 * starts fresh.
 */
function consumePending(): PendingOAuth | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_KEY);
    if (raw) localStorage.removeItem(PENDING_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingOAuth;
    if (
      !parsed ||
      typeof parsed.spaceId !== 'string' ||
      typeof parsed.serverId !== 'string' ||
      typeof parsed.ts !== 'number'
    ) {
      return null;
    }
    if (Date.now() - parsed.ts > PENDING_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Look up the MCP server's display name via the stashed (spaceId, serverId).
 * Best-effort: returns undefined if the user isn't authenticated, the
 * server no longer exists, or the API errors. Never throws — the page
 * renders the generic copy on failure.
 */
function useServerName(pending: PendingOAuth | null): string | undefined {
  const [name, setName] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!pending) return;
    let cancelled = false;

    (async () => {
      try {
        const server = await spaceApi(pending.spaceId).mcpServers.get(
          pending.serverId,
        );
        if (!cancelled && server?.name) setName(server.name);
      } catch {
        // 401 (not signed in) / 404 / network error — fall back silently.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pending]);

  return name;
}
